import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Check } from "lucide-react";
import { useState } from "react";

// Illustrative only, not live objective data.
const GROUPS = [
  { name: "Ones We Watched Pro Challenge", games: 6, rewards: 4 },
  { name: "Premier League Milestones", games: 8, rewards: 5 },
  { name: "Live Event: Weekend Warm-up", games: 3, rewards: 2 },
  { name: "Season Objectives", games: 10, rewards: 6 },
  { name: "LALIGA Gold Rush", games: 5, rewards: 3 },
  { name: "Evolution: Squad Foundations", games: 4, rewards: 2 },
];

export const PickDemo = () => {
  const { t } = useTranslation("objectives");
  const [selected, setSelected] = useState<number[]>([0, 1, 2, 3]);

  const chosen = GROUPS.filter((_, i) => selected.includes(i));
  const rewards = chosen.reduce((sum, g) => sum + g.rewards, 0);
  const separately = chosen.reduce((sum, g) => sum + g.games, 0);
  const optimized = chosen.length
    ? Math.max(...chosen.map((g) => g.games), Math.round(separately * 0.55))
    : 0;

  const toggle = (i: number) =>
    setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  return (
    <div className="overflow-hidden rounded-2xl border border-white/8 bg-[#1c1c1c]">
      <div className="flex items-start justify-between gap-4 border-b border-white/6 px-[22px] py-[18px]">
        <div>
          <span className="text-[15px] font-bold">
            {t("landing.pick.groups")}
          </span>
          <p className="mt-1 text-[13px] text-neutral-400">
            {t("landing.pick.hint")}
          </p>
        </div>
        <span className="shrink-0 text-[13px] text-neutral-500">
          {t("landing.pick.selected", { count: chosen.length })}
        </span>
      </div>
      <div className="flex flex-col p-2">
        {GROUPS.map((g, i) => {
          const on = selected.includes(i);
          return (
            <button
              key={g.name}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(i)}
              className="flex items-center gap-3.5 rounded-[10px] px-3.5 py-3 text-left hover:bg-neutral-800"
            >
              <span
                className={cn(
                  "grid size-5 flex-none place-items-center rounded-md border text-white",
                  on
                    ? "border-primary-action bg-primary-action"
                    : "border-neutral-600",
                )}
              >
                {on && <Check className="size-3.5" strokeWidth={3.5} />}
              </span>
              <span className="min-w-0 flex-1 text-[15px] font-medium text-neutral-100">
                {g.name}
              </span>
              <span className="hidden text-[13px] tabular-nums text-neutral-500 mobile:inline">
                {t("landing.pick.groupMeta", {
                  rewards: g.rewards,
                  games: g.games,
                })}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-3 border-t border-white/6 bg-neutral-900">
        <Total label={t("landing.pick.rewards")} value={rewards} />
        <Total
          label={t("landing.pick.separately")}
          value={t("landing.pick.games", { count: separately })}
          valueClassName="text-neutral-500 line-through"
        />
        <Total
          label={t("landing.pick.optimized")}
          value={t("landing.pick.games", { count: optimized })}
          labelClassName="text-complete"
          valueClassName="text-complete"
        />
      </div>
    </div>
  );
};

const Total = ({
  label,
  value,
  labelClassName,
  valueClassName,
}: {
  label: string;
  value: string | number;
  labelClassName?: string;
  valueClassName?: string;
}) => (
  <div className="px-4 py-[18px] mobile:px-[22px] [&:not(:first-child)]:border-l [&:not(:first-child)]:border-white/6">
    <div className={cn("text-xs text-neutral-500", labelClassName)}>
      {label}
    </div>
    <div
      className={cn(
        "mt-1 text-xl font-extrabold tabular-nums mobile:text-2xl",
        valueClassName,
      )}
    >
      {value}
    </div>
  </div>
);
